// DataMethodology — public policy doc on how we normalize hospital chargemasters
// into comparable prices. Same visual language as /home-beta: patriotic palette,
// Instrument Serif headlines, eyebrow+serif section headers, gold accents.
//
// This page is the slow-changing rules + rationale. Day-to-day ingests, feature
// ships, and dataset milestones live on /release-notes — surfaced via a hand-off
// card up top so readers don't assume this page reflects today's state.

import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, ArrowUpRight, Award, CheckCircle2, Database,
  Globe, Layers, Mail, ScrollText, ShieldCheck, TrendingUp,
} from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628", bgAlt: "#050e1a", bgInverse: "#FAF9F6",
        text: "#FAF9F6", textOnInverse: "#1B1B1B",
        textMuted: "rgba(250,249,246,0.6)", textMutedOnInverse: "rgba(27,27,27,0.62)",
        gold: "#C5A55A", red: "#B22234", blue: "#5B9BD5", navy: "#002868",
        cardBg: "#0d1e35", cardBorder: "rgba(250,249,246,0.10)",
        cardBgOnInverse: "#ffffff", cardBorderOnInverse: "rgba(27,27,27,0.08)",
        divider: "rgba(250,249,246,0.10)", chipBg: "rgba(197,165,90,0.10)",
        codeBg: "rgba(250,249,246,0.06)",
      };
    }
    return {
      bg: "#FAF9F6", bgAlt: "#ffffff", bgInverse: "#0a1628",
      text: "#1B1B1B", textOnInverse: "#FAF9F6",
      textMuted: "rgba(27,27,27,0.62)", textMutedOnInverse: "rgba(250,249,246,0.65)",
      gold: "#C5A55A", red: "#B22234", blue: "#002868", navy: "#002868",
      cardBg: "#ffffff", cardBorder: "rgba(27,27,27,0.08)",
      cardBgOnInverse: "#0d1e35", cardBorderOnInverse: "rgba(250,249,246,0.10)",
      divider: "rgba(27,27,27,0.08)", chipBg: "rgba(197,165,90,0.10)",
      codeBg: "rgba(27,27,27,0.06)",
    };
  }, [isDark]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const DataMethodology = () => {
  const brand = useBrand();
  const { t } = useTranslation("legal");
  const T = useThemeTokens();
  const { isDark } = useTheme();

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: sans, paddingBottom: 96 }}>
      <Helmet>
        <title>{t("dataMethodology.meta.pageTitle", { brandName: brand.name })}</title>
        <meta name="description" content={t("dataMethodology.meta.description", { brandName: brand.name })} />
        <link rel="canonical" href={`${brand.primaryDomain}/data-methodology`} />
        <meta property="og:title" content={t("dataMethodology.meta.pageTitle", { brandName: brand.name })} />
        <meta property="og:description" content={t("dataMethodology.meta.description", { brandName: brand.name })} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/data-methodology`} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={t("dataMethodology.meta.pageTitle", { brandName: brand.name })} />
        <meta name="twitter:description" content={t("dataMethodology.meta.description", { brandName: brand.name })} />
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 96, paddingBottom: 72 }}>
          <div
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-6"
            style={{ color: T.gold, fontWeight: 600 }}
          >
            <span style={{ width: 24, height: 1, background: T.gold, display: "inline-block" }} />
            Methodology
          </div>
          <h1
            style={{
              fontFamily: serif,
              fontSize: "clamp(2.4rem, 5.2vw, 4.8rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.02em",
              color: T.text,
              margin: 0,
              maxWidth: 880,
            }}
          >
            How we turn obfuscated chargemasters into comparable prices.
          </h1>
          <p
            style={{
              color: T.textMuted,
              fontSize: "1.15rem",
              lineHeight: 1.55,
              marginTop: 28,
              maxWidth: 700,
            }}
          >
            Every price on {brand.name} flows through a seven-step pipeline.
            This page is the policy doc — the slow-changing rules and the reasons behind them.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-9">
            <Link
              to="/release-notes"
              className="inline-flex items-center gap-2"
              style={{
                background: T.navy, color: "#FAF9F6",
                padding: "12px 22px", borderRadius: 4,
                fontWeight: 600, fontSize: "0.92rem", textDecoration: "none",
                boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,40,104,0.18)"}`,
              }}
            >
              <ScrollText className="w-4 h-4" />
              What changed recently
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <span
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: T.gold, padding: "10px 14px", borderRadius: 999,
                border: `1px solid ${T.gold}`, fontWeight: 500, background: T.chipBg,
              }}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Sourced from hospitals' own published files
            </span>
          </div>

          <p style={{ color: T.textMuted, fontSize: "0.85rem", marginTop: 24, maxWidth: 700, lineHeight: 1.5 }}>
            <strong style={{ color: T.text }}>Don't assume this page reflects what shipped today.</strong>{" "}
            Day-to-day ingests, methodology refinements, and feature ships are logged on the release notes
            page as they happen. The rules below are the ones that have been audited and committed.
          </p>
        </div>
      </section>

      {/* ── The Problem ─────────────────────────────────────────────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow="The problem"
            title="Required to publish. Designed to obscure."
            sub="While hospitals are legally required to publish price-transparency files under CMS regulations, many intentionally make the data difficult to use. We documented every obfuscation tactic — and built the pipeline to reverse them."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            <ObfuscationCard T={T} title="Fragmented Data" body="Splitting pricing across thousands of rows with slight variations in dosage, units, or settings." />
            <ObfuscationCard T={T} title="Inconsistent Coding" body="Using proprietary internal codes instead of standard CPT, DRG, or HCPCS." />
            <ObfuscationCard T={T} title="Missing Information" body="Omitting critical details: inpatient/outpatient designation, unit measurement, dosage amounts." />
            <ObfuscationCard T={T} title="Format Chaos" body="Publishing data in non-standard formats — PDFs, obscure CSVs, broken JSON." />
            <ObfuscationCard T={T} title="Vague Descriptions" body="Unclear procedure names that make it impossible to compare across hospitals." />
            <ObfuscationCard T={T} title="Payer-Row Inflation" body="91 rows for one hip replacement — one per insurance payer — drowning the single sticker price uninsured patients actually pay." />
          </div>

          <p style={{ color: T.textMuted, fontSize: "0.95rem", lineHeight: 1.55, marginTop: 28, maxWidth: 740 }}>
            This deliberate obfuscation makes it nearly impossible for consumers to compare prices between
            hospitals or understand what they'll actually pay. Our pipeline exists to reverse it.
          </p>
        </div>
      </section>

      {/* ── The Pipeline (7 steps) ─────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow="The pipeline"
            title="Seven steps from raw chargemaster to comparable price."
            sub={`${brand.name} employs a sophisticated normalization pipeline to transform intentionally obfuscated chargemaster data into comparable, actionable pricing insights.`}
          />

          {/* Step 1 */}
          <StepCard T={T} step={1} title="Data Ingestion & Standardization">
            <P T={T}>We process hospital transparency files through the following steps:</P>
            <List T={T} items={[
              "Extract pricing data from various formats (JSON, CSV, Excel, PDF)",
              "Map hospital-specific column names to our standardized schema",
              "Identify and extract standard medical codes (CPT, HCPCS, DRG, Revenue, NDC)",
              "Parse dosage information, unit measurements, and service settings (inpatient/outpatient)",
              "Validate data integrity and flag incomplete or suspicious entries",
            ]} />
            <SubHead T={T}>Price Selection Logic</SubHead>
            <P T={T}>When determining the final price for a procedure, we follow this priority order:</P>
            <List T={T} items={[
              <><strong>Gross Charge:</strong> If available, we use the hospital's published gross/standard charge.</>,
              <><strong>Negotiated Rates:</strong> If no gross charge exists, we analyze minimum and maximum negotiated rates.</>,
              <><strong>Fixed Rate Scenario:</strong> When MIN_RANGE and MAX_RANGE are identical and no gross_charge is provided, we use that single negotiated rate as the price.</>,
            ]} />
            <Note T={T}>
              Example: If a hospital lists CPT 99213 with MIN_RANGE=$150, MAX_RANGE=$150, and gross_charge=null,
              we record the price as $150. This often occurs when hospitals only negotiate a single standard rate
              across all payers for a given procedure.
            </Note>
            <SubHead T={T}>Price Filtering to Prevent Gaming</SubHead>
            <P T={T}>We exclude pricing records that fall outside reasonable bounds to prevent hospitals from manipulating their average prices:</P>
            <List T={T} items={[
              <><strong>Price too low:</strong> gross_charge &lt; $0.11 (excludes $0.01 placeholder prices for discontinued procedures).</>,
              <><strong>Price too high:</strong> gross_charge ≥ $999,999,999 (excludes data-entry errors and placeholder values).</>,
            ]} />
            <Note T={T}>
              <strong>Why this matters:</strong> Some hospitals list hundreds of discontinued or unavailable procedures
              at $0.01 to artificially lower their overall average prices in comparison tools. By requiring a minimum
              price of $0.11, only real, actively-offered procedures are included in our pricing analytics.
            </Note>
          </StepCard>

          {/* Medical Coding Disclaimers — sits between Step 1 and Step 2 in the original.
              Stays in the same place; treated as a sidebar card with the blue/legal accent. */}
          <DisclaimerCard T={T} brandName={brand.name} />

          {/* Step 2 */}
          <StepCard T={T} step={2} title="Price Aggregation by Code + Setting + Dosage">
            <SubHead T={T}>Core Aggregation Logic</SubHead>
            <P T={T}>For each medical code type (CPT/HCPCS, DRG, REV, NDC), we group pricing records by:</P>
            <List T={T} items={[
              <><strong>Medical Code:</strong> The standardized procedure/drug/service identifier.</>,
              <><strong>Inpatient/Outpatient Setting:</strong> Whether the service is performed inpatient or outpatient.</>,
              <><strong>Dosage/Unit Information:</strong> The specific dosage amount or unit quantity (when available).</>,
            ]} />
            <SubHead T={T}>Minimum Data Requirements</SubHead>
            <P T={T}>To build a reliable average price index for a specific code + setting + dosage combination, we require:</P>
            <List T={T} items={[
              <><strong>Minimum 3 price points:</strong> At least 3 separate pricing records with the same code + setting + dosage.</>,
              "This ensures enough data to calculate meaningful averages and identify outliers.",
              "Single or double price points are stored but not used for comparative analytics.",
            ]} />
          </StepCard>

          {/* Step 3 */}
          <StepCard T={T} step={3} title="Dosage Variance Aggregation">
            <P T={T}>
              Many hospitals list the same drug or procedure multiple times with different dosages
              (e.g., "Drug X 10mg", "Drug X 20mg", "Drug X 50mg"). To provide comprehensive pricing:
            </P>
            <List T={T} items={[
              "We normalize prices per unit when dosage information is available.",
              "Calculate per-mg, per-ml, or per-unit pricing to enable dosage-to-dosage comparisons.",
              "Aggregate all dosage variants into a single average when comparing across hospitals.",
              "Flag significant pricing discrepancies between different dosages of the same medication.",
            ]} />
            <Note T={T}>
              Example: If a hospital lists Medication X at $100 for 10mg, $180 for 20mg, and $400 for 50mg,
              we calculate per-mg pricing ($10/mg, $9/mg, $8/mg respectively) and use the median for comparisons.
            </Note>
          </StepCard>

          {/* Step 4 */}
          <StepCard T={T} step={4} title="Average Price Index Calculation">
            <P T={T}>For each code + setting + dosage combination with 3+ price points, we calculate:</P>
            <List T={T} items={[
              <><strong>Mean Price:</strong> Average of all prices for the given combination.</>,
              <><strong>Median Price:</strong> Middle value to reduce impact of extreme outliers.</>,
              <><strong>Price Range:</strong> Minimum and maximum prices observed.</>,
              <><strong>Standard Deviation:</strong> Measure of price variability.</>,
            ]} />
            <P T={T}>
              These aggregated indices are then used as the baseline for comparing individual hospital pricing
              against national or regional averages.
            </P>
          </StepCard>

          {/* Step 5 */}
          <StepCard T={T} step={5} title="Outlier Detection & Price Gouging Identification">
            <SubHead T={T}>Outlier Threshold: 50% Above Average</SubHead>
            <P T={T}>We classify a hospital's price as an "outlier" or potential price gouging instance when:</P>
            <List T={T} items={[
              <>The hospital's price for a given procedure is <strong>50% or more above the national average</strong>.</>,
              "This threshold is based on statistical analysis showing prices 1.5x+ the average are anomalous.",
              "Outlier percentage is calculated as: (Outlier Count / Total Procedures) × 100.",
            ]} />
            <SubHead T={T}>Example Calculation</SubHead>
            <List T={T} items={[
              "National average for CPT 29827 (knee arthroscopy): $3,000",
              <>Hospital A charges: $5,200 (73% above average) → <span style={{ color: T.red, fontWeight: 700 }}>OUTLIER</span></>,
              <>Hospital B charges: $3,800 (27% above average) → Within normal range</>,
              <>Hospital C charges: $2,600 (13% below average) → Below average</>,
            ]} />
          </StepCard>

          {/* Step 6 — the long one. Markup calculation, then 4 sub-cards for the
              data-quality / exclusion rules. */}
          <StepCard T={T} step={6} title="Markup Percentage Calculation">
            <SubHead T={T}>Gross Charge (Sticker Price) Only</SubHead>
            <P T={T}>
              We use <strong>gross charges only</strong> — the list price hospitals charge uninsured patients.
              Many hospitals publish chargemasters with one row per insurance payer (e.g., 91 rows for "Hip
              Replacement" — one for Aetna at $1,680, one for Blue Cross at $2,100, one for United at $8,500,
              and one gross charge at $31,000). We <strong>deduplicate to the maximum gross charge per
              procedure code</strong> — the sticker price — and discard payer-specific negotiated rates.
              This ensures we compare what hospitals actually charge, not what insurers negotiate.
            </P>
            <P T={T}>For each hospital and each procedure category (CPT, HCPCS, DRG, REV, NDC, Supply), we calculate:</P>
            <List T={T} items={[
              <><strong>Individual Markup:</strong> ((Hospital Gross Charge − National Average) / National Average) × 100</>,
              <><strong>Category Average Markup:</strong> Average markup across all deduplicated procedures in that category.</>,
              <><strong>Overall Hospital Markup:</strong> Weighted average across CPT, HCPCS, MS-DRG, Revenue, NDC, and Supply categories.</>,
            ]} />

            <SubHead T={T}>Revenue Code Bucketing (How We Make REV Comparable)</SubHead>
            <P T={T}>
              Revenue codes (REV) are department-level billing categories (e.g., REV 0360 "Operating Room
              Services") that hospitals can use for very different items at very different prices. To compare
              hospitals fairly on REV codes, we bucket each charge by{" "}
              <strong>(revenue code × inpatient/outpatient setting × normalized unit × dosage quantity)</strong>{" "}
              and only compare a hospital's charge against the same bucket nationally. A hospital's
              "REV 0272 / outpatient / each / 1" charge is compared only against other hospitals reporting that
              exact bucket — not against unrelated REV 0272 entries that mean something else. With bucketing
              in place, REV markups are now in the same range as CPT/HCPCS/MS-DRG and are included in each
              hospital's overall ranking.
            </P>
            <Note T={T}>
              Per-hospital opt-out: when an audit identifies a specific hospital whose revenue-code data is
              unreliable (e.g., per-unit quantities that don't match clinical reality), that hospital is
              excluded from REV-based ranking via the <Code T={T}>hospital_code_type_exclusions</Code> table
              while still being scored on all other code types.
            </Note>

            <SubHead T={T}>Outlier Capping (Winsorization)</SubHead>
            <P T={T}>
              Individual procedure markups are capped at <strong>10,000%</strong> (100× the national average)
              and floored at <strong>−99%</strong> before averaging. This is a standard statistical technique
              called Winsorization that prevents a single extreme line item from dominating a hospital's
              overall score.
            </P>
            <P T={T}>
              For example, if a hospital charges $268,000 for an IV insertion that nationally averages $226
              (a 118,000% markup), that procedure is counted as 10,000% in the hospital's average — still the
              worst possible score for that item, but it doesn't single-handedly determine the hospital's
              overall grade across thousands of other procedures.
            </P>

            <SubHead T={T}>Chargemaster Data Quality Exclusions</SubHead>
            <P T={T}>
              Some hospitals publish chargemaster files with systemic data quality issues — most commonly,
              assigning incorrect NDC (drug) codes to high-cost therapies. For example, a hospital might list
              a $3.9 million CAR-T cancer therapy under an NDC code that actually belongs to a $2 generic
              medication. When our system compares prices using that code, the result is a meaningless
              4,000,000% "markup" that has nothing to do with the hospital's actual pricing practices.
            </P>
            <P T={T}>
              Hospitals with these systemic issues are flagged as having <strong>chargemaster errors</strong>{" "}
              and excluded from rankings and aggregate statistics. They remain visible on their individual
              pages with a note explaining the data quality concern.{" "}
              <strong>~2,600 hospitals with published data are currently excluded from the rankings</strong>{" "}
              for various reasons (insufficient comparable procedures, duplicate NPIs/addresses, broken or
              missing chargemaster URLs, specialty/psych/rehab facilities whose billing model isn't
              comparable to acute care); a smaller number are excluded specifically for detected
              chargemaster data errors. Of the 9,500+ hospitals we track overall, 4,877 currently have
              enough comparable data to be graded and ranked.
            </P>

            <SubHead T={T}>Data Freshness</SubHead>
            <P T={T}>
              Hospitals republish their price-transparency files on their own schedules, and we
              re-ingest them on a rolling basis. That means each hospital's data has its own
              vintage — the year of the published file our current import came from — and that
              vintage is shown directly on the hospital's profile page next to the link to its
              original file. When a hospital publishes a newer file, the previous vintage is
              preserved in our archive (nothing is discarded), the new file replaces it on the
              live site, and the hospital's grade and rank are recomputed from the new data.
            </P>

            <SubHead T={T}>Facility Type Exclusions</SubHead>
            <P T={T}>
              Certain facility types are excluded from the main hospital rankings because their billing
              models are fundamentally incompatible with acute care episode pricing:
            </P>
            <List T={T} items={[
              <><strong>Long-Term Acute Care (LTAC):</strong> Facilities like Select Specialty Hospital publish a single flat per-diem rate for ALL DRG codes (e.g., $2,517/day regardless of diagnosis). Comparing one day's LTAC rate against a full acute care episode ($12k–$45k) produces misleading −87% markup figures.</>,
              <><strong>Rehabilitation Hospitals:</strong> Inpatient rehab facilities (Encompass Health, Banner Rehab) bill using CMG (Case Mix Group) codes specific to rehab, not standard CPT/DRG. Their chargemasters are 95–99% drug formulary items.</>,
              <><strong>Psychiatric/Behavioral Health:</strong> Psych hospitals bill per-diem room-and-board rates under revenue codes rather than procedure-level CPT or DRG codes used by acute care facilities.</>,
              <><strong>Insufficient Data:</strong> Hospitals with fewer than 50 comparable procedures are not assigned a national rank — too few data points for a defensible markup calculation. These hospitals still appear on their individual profile pages with all chargemaster data intact.</>,
            ]} />
            <Note T={T}>
              Excluded hospitals remain fully visible on their individual profile pages with all chargemaster
              data intact. They are only removed from the comparative rankings where their billing model
              would distort the results.
            </Note>

            <SubHead T={T}>Code-Level Exclusions (Inherently Uncomparable)</SubHead>
            <P T={T}>
              Beyond hospital-level exclusions, certain codes are excluded from all ranking comparisons
              because they are fundamentally not comparable across hospitals:
            </P>
            <List T={T} items={[
              <><strong>Placeholder NDC codes (00000-prefix):</strong> NDC codes beginning with <Code T={T}>00000-</Code> are placeholder identifiers that hospitals use for in-house compounded preparations. Different hospitals use the same placeholder NDC for completely different products.</>,
              <><strong>HCPCS catch-all codes:</strong> <Code T={T}>A9270</Code> ("non-covered item"), <Code T={T}>J9999</Code>, <Code T={T}>J7999</Code>, <Code T={T}>J3590</Code>, and <Code T={T}>J8499</Code> are statutory "not otherwise classified" codes used for thousands of unrelated drugs and items. National averages for them are inherently meaningless.</>,
              <><strong>Manufacturer-name descriptions:</strong> Some imports incorrectly map manufacturer names (e.g., "TEVA PHARMACEUTICALS") into the description field instead of the actual drug name. These rows are detected and excluded.</>,
            ]} />

            {/* Unit-quantity sanity rules — accent box (amber/red) */}
            <AccentBox T={T} variant="amber" title="Unit-Quantity Sanity Rules (Source-Row Cleanup)">
              <P T={T}>
                Before national averages are computed, we apply deterministic sanity rules to individual
                chargemaster rows to detect when <Code T={T}>unit_quantity</Code> has been misused. The CMS
                spec defines this field as the number of billable units on one line item (e.g.,{" "}
                <em>1 IV bag</em>, <em>30 tablets</em>). In practice, some hospitals overload it with values
                that are actually drug-strength descriptors — for example,{" "}
                <em>"FLORAJEN 20 BILLION CELL"</em> imports as <Code T={T}>unit_quantity = 20,000,000,000</Code>.
                When these values pass through aggregation unfiltered, they corrupt per-unit national averages.
              </P>
              <P T={T}>
                Each row that triggers a rule is preserved (never deleted) but marked{" "}
                <Code T={T}>is_data_error = true</Code> so it drops out of national averages and rankings.
                The original value is preserved in <Code T={T}>original_unit_quantity</Code>, the rule that
                fired is recorded in <Code T={T}>data_error_reason</Code>, and all fired rules accumulate in{" "}
                <Code T={T}>quality_flags TEXT[]</Code>.
              </P>
              <SubHead T={T} small>Rule 1: Magnitude Ceiling</SubHead>
              <P T={T}>
                Even legitimate bulk packaging tops out around 1,000. Anything above is almost certainly a
                parsing artifact. <strong>Detection rule:</strong>{" "}
                <Code T={T}>unit_quantity &gt; 1,000</Code> → row is marked <Code T={T}>is_data_error = true</Code>{" "}
                with reason <Code T={T}>'absurd_magnitude'</Code>, and working <Code T={T}>unit_quantity</Code>{" "}
                is reset to 1 so downstream math can't blow up.
              </P>
              <SubHead T={T} small>Rule 4: Episode-Level Code Plausibility</SubHead>
              <P T={T}>
                DRG, APR-DRG, and CMG describe an entire admission or rehab stay, so any quantity other
                than 1 is a data error. <strong>Detection rule:</strong>{" "}
                <Code T={T}>unit_quantity &gt; 1</Code> AND row has any of <Code T={T}>drg_code</Code>,{" "}
                <Code T={T}>apr_drg_code</Code>, or <Code T={T}>cmg_code</Code> populated.
              </P>
              <P T={T}>
                <strong>Why no per-code caps for CPT, HCPCS, NDC, drug, supply, or revenue?</strong> These
                code families have legitimate uses across the full 1–1,000 range. Time-based CPT codes are
                billed in 15-minute units (a 4-hour cardiac procedure = 16+ units). Drugs are billed by mass.
                Rule 1's flat 1,000 ceiling is the only plausibility check we apply.
              </P>
            </AccentBox>

            {/* Revenue Code Caution Flags — accent box */}
            <AccentBox T={T} variant="amber" title="Revenue Code Caution Flags">
              <P T={T}>
                Revenue codes are now included in the overall hospital ranking (bucketed by setting × unit ×
                dosage), but our automated audit pipeline still flags three specific patterns where REV data
                may be unreliable. Hospitals matching these patterns are individually opted out of REV-based
                ranking via <Code T={T}>hospital_code_type_exclusions</Code> while still being scored on
                every other code type.
              </P>
              <SubHead T={T} small>Pattern 1: Corrupted National Average (unit-quantity mismatch)</SubHead>
              <P T={T}>
                Some chargemasters contain revenue code entries with per-unit quantities wildly inconsistent
                with clinical reality (e.g., REV 0200 ICU room with unit_quantity 4,000, producing a national
                per-unit price of $0.006 instead of a normal per-day rate). The detection rule flags any
                hospital whose revenue codes match national-average entries where{" "}
                <Code T={T}>(avg_charge / unit_quantity) &lt; $0.10/unit</Code>.
              </P>
              <SubHead T={T} small>Pattern 2: Average Markedly Above Median (divergence signal)</SubHead>
              <P T={T}>
                If a hospital's revenue average markup is more than <strong>3× the revenue median markup</strong>{" "}
                AND the average exceeds 300%, this strongly suggests a small number of extreme codes are
                pulling the average. Detection rule:{" "}
                <Code T={T}>rev_avg_markup &gt; (rev_median_markup × 3) AND rev_avg_markup &gt; 300%</Code>.
              </P>
              <SubHead T={T} small>Pattern 3: Sentinel / Placeholder Prices</SubHead>
              <P T={T}>
                Some hospitals publish revenue code rows with round-number sentinel prices — $99,999.99 or
                $999,999.00 — to indicate "not applicable" or "price on request." We scan for these specific
                sentinel values and flag any hospital with one or more matching rows.
              </P>
              <Note T={T}>
                <strong>Why flags instead of exclusions?</strong> Revenue code caution flags often reflect
                limitations in the national reference data rather than the hospital's chargemaster.
                Excluding these hospitals would remove legitimate pricing information. We surface the flag
                so readers can weigh the revenue markup figures with context.
              </Note>
            </AccentBox>
          </StepCard>

          {/* Step 7 */}
          <StepCard T={T} step={7} title="Medical Scenario Cost Estimation">
            <P T={T}>
              Individual procedure prices are useful, but consumers really need to know:{" "}
              <strong>"How much will this entire medical event cost me?"</strong> A knee replacement isn't
              just one surgery code — it's pre-op bloodwork, anesthesia, the surgery, OR fees, implant costs,
              hospital room charges, physical therapy, and follow-up visits. We combine all of these into a
              single <strong>scenario cost estimate</strong>.
            </P>

            <SubHead T={T}>Two Types of Scenarios</SubHead>
            <AccentBox T={T} variant="green" title="Curated Scenarios (Free)">
              <P T={T}>
                We maintain a library of pre-built scenarios for the most common medical events — surgeries,
                ER visits, chronic conditions, cancer treatments, and more. Each is designed by combining
                medical knowledge with real billing code data to model a typical clinical pathway from start
                to finish. Available to everyone at no cost.
              </P>
            </AccentBox>
            <AccentBox T={T} variant="gold" title="Custom Scenarios ($0.99)">
              <P T={T}>
                Have a specific situation that isn't in our curated library? For $0.99, build a custom
                scenario tailored to your exact situation. You describe your medical event, answer a few
                clinical detail questions, and our AI builds a validated scenario priced from the national
                chargemaster average — the same methodology and data quality standards as our curated scenarios.
              </P>
              <P T={T}>
                Custom scenarios come with a <strong>shareable URL</strong> so you can send the cost
                breakdown to family, your doctor, or your insurance company. Public or unlisted, your choice.
              </P>
            </AccentBox>

            <SubHead T={T}>How Every Scenario Is Built</SubHead>
            <P T={T}>The same rigorous process applies to curated and custom scenarios:</P>
            <List T={T} items={[
              <><strong>Clinical pathway modeling:</strong> Each scenario maps the full journey — from initial evaluation through procedure, recovery, and follow-up — broken into phases with day-by-day timelines.</>,
              <><strong>Standard billing codes:</strong> Every line item uses standard medical billing codes (CPT, REV, DRG, HCPCS).</>,
              <><strong>Pricing validation:</strong> We require <strong>80% or more</strong> of codes in each scenario to have a national chargemaster average sourced from 3+ hospitals nationwide. Scenarios below this threshold are flagged or rejected.</>,
              <><strong>Realistic quantities:</strong> Each code has a clinically appropriate quantity (e.g., 3 days ICU, 4 lab draws, 2 follow-up visits).</>,
            ]} />

            <SubHead T={T}>Outlier Prevention — How We Keep Garbage Data Out</SubHead>
            <P T={T}>
              Raw min/max values can contain garbage outliers — a hospital might list a knee replacement at
              $0.16 (placeholder) or an IV bag at $500,000 (data entry error). To prevent these from
              distorting the cost range you see:
            </P>
            <List T={T} items={[
              <><strong>Minimum price floor:</strong> Low-end estimates are clamped to no less than <strong>10% of the national median</strong>.</>,
              <><strong>Maximum price ceiling:</strong> High-end estimates are clamped to no more than <strong>20× the national median</strong>.</>,
            ]} />
            <Note T={T}>
              Example: If the national median for knee replacement (CPT 27447) is $25,000, the minimum used
              in the scenario will be at least $2,500 — not $0.16 from some hospital's placeholder — and the
              maximum will be capped at $500,000 — not $9,999,999 from a data entry error.
            </Note>

            <SubHead T={T}>Per-Code-Type Bucket Selection</SubHead>
            <P T={T}>
              Each chargemaster line carries a billing unit — per-mg, per-ml, per-IU, per-15-min, per-vial, or
              "other" when the line is a single per-encounter charge with no unit dimension. The scenario cost
              engine selects which buckets feed each code's national price based on what's clinically meaningful
              for that family of codes:
            </P>
            <List T={T} items={[
              <><strong>CPT (procedure / E&M codes)</strong> — surgical packages like Vaginal Delivery (59400), Critical Care (99291), and office visits (99213) are billed once per encounter with no per-mg or per-hour dimension. Their entire chargemaster universe lives in the "other" bucket, so it's <em>included</em> in the national average. The "unspecified" bucket (where the importer couldn't classify the line at all) is excluded.</>,
              <><strong>HCPCS J-codes (drugs)</strong> — billed at per-mg, per-ml, or per-IU resolution. We use only those clean unit buckets and <em>exclude</em> the "other" bucket entirely, because for drug codes that bucket is per-vial total dollars and inflates the national average by 100–600%.</>,
              <><strong>HCPCS A/E/L/G codes (supplies, DME)</strong> — per-encounter items with no per-unit dimension. When clean unit buckets exist for these codes we use them; otherwise we allow the "other" bucket the same way CPT does. J-codes specifically never fall into this fallback.</>,
              <><strong>REV (revenue / department codes)</strong>, <strong>NDC (drugs)</strong>, <strong>supply codes</strong> — strictly exclude the "other" and "unspecified" buckets. For these codes "other" is where unit-confused per-line / per-day charges get dumped (e.g. ICU room/board lines mistakenly tagged with a per-mL value), and including them pulls the national average toward unrelated services.</>,
              <><strong>DRG (admission bundles)</strong> — no unit dimension at all. Bundle prices are used directly.</>,
            ]} />
            <Note T={T}>
              The per-procedure breakdown rows on a scenario page and the scenario's headline total are both
              computed with this same per-code-type filter, so the totals always reconcile to what's visible
              in the line items below them.
            </Note>

            <SubHead T={T}>REV+CPT Paired Anchoring (Per-Procedure Revenue-Code Pricing)</SubHead>
            <P T={T}>
              Revenue codes (REV) are department-level billing buckets — REV 0360 is "OR services" whether
              the surgery was a c-section, a tibial fracture repair, or a cardiac device removal. A single
              national average for REV 0360 mixes wildly different procedures and produces a number that
              doesn't represent any specific surgery's OR fee.
            </P>
            <P T={T}>
              For scenarios where a REV row accompanies a specific CPT procedure, we compute{" "}
              <strong>paired national averages</strong> from chargemaster rows where both codes co-occur on
              the same line. A c-section scenario's REV 0360 row is anchored to CPT 59510, and the lookup
              uses the OR fees of hospitals that bill REV 0360 alongside CPT 59510 — a much tighter,
              procedure-specific pool than the all-of-REV-0360 noise.
            </P>
            <P T={T}>
              Inside each paired bucket, individual hospital charges are{" "}
              <strong>winsorized at 25× the bucket median</strong> before averaging. This guardrail caps
              data errors and sentinel prices without trimming legitimate 20× outlier hospitals — those
              still register as outliers in the bucket. The cap fires on data quality issues, not on real
              high-charging facilities.
            </P>
            <Note T={T}>
              Pairs are only published when ≥3 hospitals report both codes on the same chargemaster row.
              Scenarios without a viable pair fall back to the bucket-filtered global REV average from
              Step 6 — coverage is never gated by anchoring.
            </Note>

            <SubHead T={T}>DRG Bundle Architecture (Single-Stay vs Multi-Stay Scenarios)</SubHead>
            <P T={T}>
              A DRG (Diagnosis-Related Group) code is a facility-side <strong>bundle</strong> covering an
              entire inpatient admission — OR, ICU, room and board, recovery, pharmacy, supplies, all of it.
              When a scenario contains a DRG row, that DRG IS the inpatient stay's price. The cost engine
              recognizes this and folds in: every other inpatient line item in the scenario (CPT, REV,
              HCPCS rows with inpatient setting) is treated as <em>inside the bundle</em> and excluded from
              the cost sum. Outpatient items (pre-op clinic, follow-up visits, rehab, ER triage) continue
              to be summed on top because they're separately billed.
            </P>
            <P T={T}>
              Each scenario carries an <Code T={T}>is_multi_stay</Code> flag:
            </P>
            <List T={T} items={[
              <><strong>Single-stay scenarios</strong> have one continuous hospital admission and at most one DRG row. The DRG covers the entire inpatient portion; outpatient phases (pre-op, follow-up, rehab) add to it.</>,
              <><strong>Multi-stay scenarios</strong> involve multiple separate admissions (e.g., a cascading-complication case with an initial surgery, a later revision surgery, and a third admission for sepsis). Each admission has its own DRG row in the scenario; the cost engine sums all DRG bundles plus the outpatient line items.</>,
              <><strong>Pure DRG scenarios</strong> contain only a DRG (no line items) — the bundle IS the scenario. Used when itemization isn't useful or available.</>,
            ]} />
            <Note T={T}>
              The bundle and the itemized line items represent the same admission's facility charges
              priced two different ways — the DRG is what Medicare pays for the whole admission, and the
              line items are what the chargemaster lists for each service inside it. The engine prices
              the admission once using the bundle and keeps the line items visible so consumers can see
              what services that bundle covers.
            </Note>

            <SubHead T={T}>Total Scenario Cost</SubHead>
            <List T={T} items={[
              <><strong>Median estimate:</strong> Sum of (national median price × quantity) for all procedures.</>,
              <><strong>Low-end estimate:</strong> Sum of (clipped minimum × quantity) for all procedures.</>,
              <><strong>High-end estimate:</strong> Sum of (clipped maximum × quantity) for all procedures.</>,
            ]} />
            <Note T={T}>
              Procedures where national coverage is still being built are shown as unpriced line items
              rather than estimates, and the scenario's coverage percentage reflects how much of the
              total is anchored to a national chargemaster average. All scenario estimates represent{" "}
              <strong>gross charges (sticker prices)</strong> — what hospitals bill uninsured patients —
              not what insured patients typically pay after negotiated rates.
            </Note>

            <SubHead T={T}>Per-Hospital Scenario Coverage</SubHead>
            <P T={T}>For a hospital to be ranked on a scenario, three conditions all have to hold:</P>
            <OrderedList T={T} items={[
              <><strong>Cornerstone codes present.</strong> Every scenario has 1–3 codes that <em>define</em> the treatment. For CAR-T it's the actual CAR-T J-code; for coronary bypass it's the CABG CPT (33510 etc.); for ABA Intensive it's the technician treatment-time code (97153) and professional-treatment code (97155). If the hospital's chargemaster doesn't include the cornerstone codes, they aren't really offering that treatment.</>,
              <><strong>≥ 75% of remaining codes covered.</strong> Below that, the comparison is filling in too much from national averages to be meaningful.</>,
              <><strong>Apples-to-apples billing units.</strong> Each code only counts toward coverage if the hospital's listing uses the same canonical billing unit (per-15-min for ABA, per-mg for a drug, per-IU for a coagulation factor). If a hospital lists a code with no unit information ("other"), it doesn't count.</>,
            ]} />

            <SubHead T={T}>How the National Average Is Computed</SubHead>
            <P T={T}>
              For each procedure code in a scenario, we compute the national average from the{" "}
              <strong>most-populous properly-normalized billing-unit bucket</strong> with at least three
              hospitals reporting. "Properly normalized" means the unit is concrete — <Code T={T}>each</Code>,{" "}
              <Code T={T}>mg</Code>, <Code T={T}>ml</Code>, <Code T={T}>IU</Code> — not <Code T={T}>other</Code>{" "}
              or <Code T={T}>unspecified</Code>.
            </P>
            <P T={T}>
              For rare specialty codes (gene therapies, CAR-T) where only a handful of hospitals report
              normalized prices, we accept any normalized bucket with 3+ reporting hospitals. For codes
              where no normalized bucket exists, we fall back to the <Code T={T}>other</Code> bucket and
              show a "limited-benchmark" indicator.
            </P>
          </StepCard>
        </div>
      </section>

      {/* ── Hospital Letter Grades ─────────────────────────────────────── */}
      <section id="letter-grades" style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow="Grading scale"
            title="Calibrated to the world — not to the US."
            sub={`Every hospital on ${brand.name} receives a letter grade from A to F based on how its prices compare to national median prices for the same billing codes, across all procedure types.`}
            icon={<Award className="w-4 h-4" />}
          />

          <div className="mt-10 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
            <div>
              <SubHead T={T}>Why a Median Baseline?</SubHead>
              <P T={T}>
                Each hospital's overall markup measures its filed prices against the <strong>national
                median price</strong> for the same billing code. We use the median rather than the mean
                because it is resilient: a single hospital filing anomalous prices can drag a national
                average, but it cannot move the median. That makes the grade — and the national rank,
                which is built on the same number — impossible for any one chargemaster to distort.
              </P>
              <P T={T}>
                The scale stays deliberately hard. US hospital prices are dramatically higher than every
                other developed nation — even the cheapest US hospitals charge multiples of what patients
                pay in Canada or Israel for identical procedures. A scale where mid-pack US pricing earned
                an A or B would obscure that reality, so the typical US hospital lands in the middle of
                the scale, not at the top.
              </P>
              <Note T={T}>
                Under this scale, roughly 3% of US hospitals earn an A, 27% a B, 32% a C, 19% a D, and 19%
                an F. Graded the same way at the state level, the typical US state earns a C.
              </Note>
            </div>
            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 10,
                padding: 24,
              }}
            >
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", color: T.gold, fontWeight: 700, marginBottom: 14 }}>
                Grade Boundaries
              </div>
              <GradeRow T={T} grade="A" color="#22c55e" range="≤ −45%" body="45%+ below the national median — approaching international pricing." />
              <GradeRow T={T} grade="B" color="#84cc16" range="−45% to +20%" body="At or near the national median — cheaper than the typical US hospital." />
              <GradeRow T={T} grade="C" color="#eab308" range="+20% to +90%" body="Above the median — mid-pack US pricing, still far beyond international norms." />
              <GradeRow T={T} grade="D" color="#f97316" range="+90% to +175%" body="Well above the median — roughly 2–2.75× typical national prices." />
              <GradeRow T={T} grade="F" color="#ef4444" range="> +175%" body="Extreme — nearly 3× the national median or more." last />
            </div>
          </div>
        </div>
      </section>

      {/* ── International Price Comparison ─────────────────────────────── */}
      <section id="international-comparison" style={{ background: T.bgInverse, color: T.textOnInverse }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow="Global comparisons"
            title="How we map US codes to peer-nation fee schedules."
            sub="Our 'US vs World' comparison shows how US hospital prices stack up against government-regulated fee schedules in other developed nations — what Americans pay for the same procedure that costs a fraction elsewhere."
            onInverse
            icon={<Globe className="w-4 h-4" />}
          />

          <div className="mt-10 grid lg:grid-cols-2 gap-8">
            <div>
              <SubHeadInverse T={T}>Data Sources</SubHeadInverse>
              <UList T={T} onInverse items={[
                <><strong>Australia:</strong> Medicare Benefits Schedule (MBS)</>,
                <><strong>Canada:</strong> OHIP Schedule of Benefits</>,
                <><strong>France:</strong> CCAM national tariffs</>,
                <><strong>Germany:</strong> EBM uniform fee schedule</>,
                <><strong>India:</strong> CGHS rate list</>,
                <><strong>Israel:</strong> National Insurance Institute</>,
                <><strong>Japan:</strong> National Health Insurance fee schedule</>,
                <><strong>South Korea:</strong> HIRA fee schedule</>,
                <><strong>Switzerland:</strong> TARMED point-based schedule</>,
                <><strong>United Kingdom:</strong> NHS National Tariff (HRG bundles)</>,
              ]} />
            </div>
            <div>
              <SubHeadInverse T={T}>Code Crosswalk</SubHeadInverse>
              <p style={{ color: T.textMutedOnInverse, fontSize: "0.95rem", lineHeight: 1.6, marginTop: 4 }}>
                US hospitals bill using CPT codes, but other countries use their own procedure coding systems.
                We build a crosswalk by matching each CPT code to the closest foreign equivalent by clinical
                description and scope of service.
              </p>
              <UList T={T} onInverse items={[
                "Each CPT code is matched to the closest foreign code by clinical description and scope.",
                "Matches are validated to ensure clinical equivalence (same procedure, same complexity level).",
                'Where no direct match exists (e.g., UK HRG bundles vs. US per-procedure billing), we note "no match" rather than force an inaccurate comparison.',
                "All prices are converted to USD using current exchange rates (updated quarterly).",
              ]} />

              <SubHeadInverse T={T}>Multiplier</SubHeadInverse>
              <p style={{ color: T.textMutedOnInverse, fontSize: "0.95rem", lineHeight: 1.6, marginTop: 4 }}>
                <strong>Multiplier</strong> = US National Average Gross Charge / International Fee Schedule Price.
                We require at least <strong>3 matched CPT codes</strong> per scenario for a valid comparison.
              </p>
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid rgba(197,165,90,0.3)`,
                  borderRadius: 6,
                  padding: 12,
                  marginTop: 10,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "0.78rem",
                  color: T.textOnInverse,
                }}
              >
                State Multiplier = National Multiplier × (1 + State Median Markup % / 100)
              </div>
            </div>
          </div>

          <div className="mt-10">
            <SubHeadInverse T={T}>Important Caveats</SubHeadInverse>
            <UList T={T} onInverse items={[
              <><strong>Apples-to-oranges risk:</strong> Some countries bundle procedures differently (UK HRGs cover an entire episode of care, not individual procedures). We flag these and exclude poor matches.</>,
              <><strong>Fee schedule ≠ out-of-pocket:</strong> International prices are government-regulated fee schedule rates. In most countries patients pay little or nothing out-of-pocket.</>,
              <><strong>US prices are gross charges:</strong> US prices used are hospital sticker prices (gross charges) — what uninsured patients are billed. Insured patients typically pay less after negotiated rates.</>,
              <><strong>Coverage varies:</strong> Not all CPT codes have matches in every country's fee schedule. We show the number of matched codes and only include comparisons with sufficient overlap.</>,
            ]} />
          </div>
        </div>
      </section>

      {/* ── Audit it yourself ──────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow="Audit it yourself"
            title="Don't trust us. Verify the math."
            sub="We encourage all users to independently verify our calculations. The raw data is on every hospital's profile page."
            icon={<CheckCircle2 className="w-4 h-4" />}
          />

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 10,
                padding: 28,
              }}
            >
              <div className="flex items-center gap-2 mb-4" style={{ color: T.gold }}>
                <Layers className="w-4 h-4" />
                <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
                  How to verify
                </span>
              </div>
              <UList T={T} items={[
                `Visit any hospital profile page on ${brand.name}.`,
                "View the raw pricing data table at the bottom of the page.",
                "Filter by code type (CPT, DRG, REV, NDC) and inpatient/outpatient setting.",
                "Manually calculate averages and compare to our displayed analytics.",
                "Cross-reference with the hospital's official chargemaster file (linked on profile pages).",
              ]} />
            </div>
            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 10,
                padding: 28,
              }}
            >
              <div className="flex items-center gap-2 mb-4" style={{ color: T.red }}>
                <AlertTriangle className="w-4 h-4" />
                <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
                  Important disclaimer
                </span>
              </div>
              <P T={T}>
                While we make every effort to accurately normalize and aggregate hospital pricing data, our
                methodology may contain errors or limitations due to the intentional obfuscation employed by
                hospitals. We strongly recommend verifying all pricing information directly with the hospital
                before making healthcare decisions.
              </P>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact ─────────────────────────────────────────────────────── */}
      <section>
        <div className="mx-auto px-6 text-center" style={{ maxWidth: 820, paddingTop: 88, paddingBottom: 48 }}>
          <div
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-6"
            style={{ color: T.gold, fontWeight: 600, justifyContent: "center" }}
          >
            <Mail className="w-3.5 h-3.5" />
            Questions or concerns
          </div>
          <h2
            style={{
              fontFamily: serif,
              fontSize: "clamp(1.9rem, 3.8vw, 3rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: T.text,
              margin: 0,
            }}
          >
            Found an error? Tell us.
          </h2>
          <p style={{ color: T.textMuted, fontSize: "1.05rem", lineHeight: 1.55, marginTop: 16 }}>
            If you believe our data-normalization process contains errors, or if you have suggestions for
            improvement, write to us.
          </p>
          <a
            href={`mailto:${brand.contact.support}`}
            className="inline-flex items-center gap-2 mt-8"
            style={{
              background: T.navy,
              color: "#FAF9F6",
              padding: "14px 26px",
              borderRadius: 4,
              fontWeight: 600,
              fontSize: "0.95rem",
              textDecoration: "none",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            <Mail className="w-4 h-4" />
            {brand.contact.support}
          </a>
          <p style={{ color: T.textMuted, fontSize: "0.85rem", marginTop: 16 }}>
            We're committed to continuous improvement of our methodology and welcome feedback from
            healthcare professionals, data scientists, and consumers.
          </p>
          <div className="flex flex-wrap items-center gap-3 justify-center mt-8">
            <Link
              to="/release-notes"
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: T.gold,
                textDecoration: "none",
                padding: "10px 14px",
                borderRadius: 999,
                border: `1px solid ${T.gold}`,
                fontWeight: 500,
                background: T.chipBg,
              }}
            >
              <ScrollText className="w-3.5 h-3.5" />
              View release notes
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <Link
              to="/hospital-rankings"
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: T.gold,
                textDecoration: "none",
                padding: "10px 14px",
                borderRadius: 999,
                border: `1px solid ${T.gold}`,
                fontWeight: 500,
                background: T.chipBg,
              }}
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Hospital rankings
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <Link
              to="/data-sources"
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: T.gold,
                textDecoration: "none",
                padding: "10px 14px",
                borderRadius: 999,
                border: `1px solid ${T.gold}`,
                fontWeight: 500,
                background: T.chipBg,
              }}
            >
              <Database className="w-3.5 h-3.5" />
              Data sources & compliance
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataMethodology;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components — same visual language as /home-beta
// ─────────────────────────────────────────────────────────────────────────────
function SectionHeader({
  T, eyebrow, title, sub, onInverse, icon,
}: {
  T: ReturnType<typeof useThemeTokens>;
  eyebrow: string; title: string; sub: string;
  onInverse?: boolean;
  icon?: React.ReactNode;
}) {
  const textColor = onInverse ? T.textOnInverse : T.text;
  const mutedColor = onInverse ? T.textMutedOnInverse : T.textMuted;
  return (
    <div className="max-w-3xl">
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-5"
        style={{ color: T.gold, fontWeight: 600 }}
      >
        <span style={{ width: 24, height: 1, background: T.gold, display: "inline-block" }} />
        {icon}
        {eyebrow}
      </div>
      <h2
        style={{
          fontFamily: serif,
          fontSize: "clamp(1.9rem, 3.6vw, 3rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.02em",
          color: textColor,
          margin: 0,
        }}
      >
        {title}
      </h2>
      <p style={{ color: mutedColor, fontSize: "1.05rem", lineHeight: 1.55, marginTop: 16 }}>
        {sub}
      </p>
    </div>
  );
}

function ObfuscationCard({
  T, title, body,
}: { T: ReturnType<typeof useThemeTokens>; title: string; body: string }) {
  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 22,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute", top: 0, left: 0, width: 3, height: "100%",
          background: T.red,
        }}
      />
      <div className="flex items-center gap-2 mb-3" style={{ color: T.red }}>
        <AlertTriangle className="w-4 h-4" />
        <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
          Obfuscation tactic
        </span>
      </div>
      <h3 style={{ fontFamily: serif, fontSize: "1.2rem", lineHeight: 1.18, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
        {title}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.5, marginTop: 8 }}>
        {body}
      </p>
    </div>
  );
}

// StepCard — the big numbered cards used for the 7 pipeline steps. Step number
// rendered as a faint serif glyph in the top-right corner.
function StepCard({
  T, step, title, children,
}: {
  T: ReturnType<typeof useThemeTokens>;
  step: number; title: string; children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 10,
        padding: 32,
        marginTop: 24,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute", top: 0, left: 0, width: 4, height: "100%",
          background: T.gold,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 16,
          right: 24,
          fontFamily: serif,
          fontSize: "5rem",
          color: T.gold,
          opacity: 0.18,
          lineHeight: 1,
          fontWeight: 400,
          pointerEvents: "none",
        }}
        aria-hidden
      >
        {step}
      </div>
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] mb-3"
        style={{ color: T.gold, fontWeight: 700 }}
      >
        Step {step}
      </div>
      <h3
        style={{
          fontFamily: serif,
          fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)",
          lineHeight: 1.1,
          letterSpacing: "-0.015em",
          color: T.text,
          margin: 0,
          paddingRight: 60,
        }}
      >
        {title}
      </h3>
      <div className="mt-5">
        {children}
      </div>
    </div>
  );
}

// DisclaimerCard — separate from StepCard. Blue accent for legal/IP content.
function DisclaimerCard({
  T, brandName,
}: { T: ReturnType<typeof useThemeTokens>; brandName: string }) {
  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 10,
        padding: 28,
        marginTop: 24,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute", top: 0, left: 0, width: 4, height: "100%",
          background: T.blue,
        }}
      />
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] mb-3"
        style={{ color: T.blue, fontWeight: 700 }}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        Legal notices
      </div>
      <h3
        style={{
          fontFamily: serif,
          fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
          lineHeight: 1.12,
          letterSpacing: "-0.01em",
          color: T.text,
          margin: 0,
        }}
      >
        Medical Coding System Disclaimers
      </h3>
      <p style={{ color: T.text, fontWeight: 600, fontSize: "0.95rem", marginTop: 14 }}>
        Important Trademark and Copyright Notices:
      </p>
      <DisclaimerBlock T={T} label="CPT® Code Disclaimer">
        CPT® is a registered trademark of the American Medical Association (AMA). All CPT code educational
        content, procedure summaries, and code descriptions on {brandName} are independently authored by our
        team to help consumers understand hospital billing codes. We do not reproduce, use, or rely on AMA
        copyrighted text, official CPT code descriptors, or proprietary AMA materials.
      </DisclaimerBlock>
      <DisclaimerBlock T={T} label="HCPCS Code Disclaimer">
        HCPCS Level II codes are maintained by CMS. HCPCS Level I codes are CPT® codes owned by the AMA. All
        HCPCS code descriptions and educational content on {brandName} are independently authored and do not
        reproduce official code descriptors or copyrighted materials.
      </DisclaimerBlock>
      <DisclaimerBlock T={T} label="Other Medical Coding Systems">
        MS-DRG, Revenue Codes, and NDC code references are used solely for analyzing publicly disclosed
        hospital pricing data. All educational content is independently authored and does not reproduce
        official government or proprietary materials beyond what is necessary for price transparency
        analysis.
      </DisclaimerBlock>
      <p style={{ color: T.textMuted, fontSize: "0.8rem", fontStyle: "italic", borderTop: `1px solid ${T.divider}`, paddingTop: 14, marginTop: 14 }}>
        Our use of medical coding references is limited to what is necessary to fulfill the public-interest
        purpose of healthcare price transparency under CMS regulations. We respect all intellectual property
        rights and trademarks.
      </p>
    </div>
  );
}

function DisclaimerBlock({
  T, label, children,
}: { T: ReturnType<typeof useThemeTokens>; label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <p style={{ color: T.text, fontWeight: 700, fontSize: "0.82rem" }}>{label}:</p>
      <p style={{ color: T.textMuted, fontSize: "0.82rem", lineHeight: 1.55, marginTop: 4 }}>
        {children}
      </p>
    </div>
  );
}

// AccentBox — for nested rule / pattern boxes inside Step cards.
function AccentBox({
  T, variant, title, children,
}: {
  T: ReturnType<typeof useThemeTokens>;
  variant: "amber" | "green" | "gold";
  title: string;
  children: React.ReactNode;
}) {
  const color = variant === "green" ? "#2b5e49"
              : variant === "gold"  ? T.gold
              : "#e67e22"; // amber
  return (
    <div
      style={{
        marginTop: 18,
        padding: 20,
        borderRadius: 8,
        border: `1px solid ${color}40`,
        background: `${color}0c`,
      }}
    >
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] mb-3"
        style={{ color, fontWeight: 700, fontSize: "0.72rem" }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// GradeRow — single row in the letter-grade card.
function GradeRow({
  T, grade, color, range, body, last,
}: {
  T: ReturnType<typeof useThemeTokens>;
  grade: string; color: string; range: string; body: string; last?: boolean;
}) {
  return (
    <div
      className="flex items-start gap-3"
      style={{
        paddingTop: 12, paddingBottom: 12,
        borderBottom: last ? "none" : `1px solid ${T.cardBorder}`,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32, height: 32, borderRadius: "50%",
          background: color, color: "#fff",
          fontWeight: 700, fontFamily: serif, fontSize: "1.1rem",
          flexShrink: 0,
        }}
      >
        {grade}
      </span>
      <div className="flex-1 min-w-0">
        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.78rem", color, fontWeight: 700 }}>
          {range}
        </div>
        <div style={{ color: T.textMuted, fontSize: "0.85rem", lineHeight: 1.45, marginTop: 2 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

// ── Typography helpers ──────────────────────────────────────────────────────
function P({ T, children }: { T: ReturnType<typeof useThemeTokens>; children: React.ReactNode }) {
  return (
    <p style={{ color: T.textMuted, fontSize: "0.95rem", lineHeight: 1.6, marginTop: 12 }}>
      {children}
    </p>
  );
}

function SubHead({
  T, children, small,
}: { T: ReturnType<typeof useThemeTokens>; children: React.ReactNode; small?: boolean }) {
  return (
    <h4
      style={{
        fontFamily: serif,
        fontSize: small ? "1.05rem" : "1.18rem",
        lineHeight: 1.2,
        letterSpacing: "-0.005em",
        color: T.text,
        marginTop: small ? 18 : 22,
        marginBottom: 6,
      }}
    >
      {children}
    </h4>
  );
}

function SubHeadInverse({
  T, children,
}: { T: ReturnType<typeof useThemeTokens>; children: React.ReactNode }) {
  return (
    <h4
      style={{
        fontFamily: serif,
        fontSize: "1.18rem",
        lineHeight: 1.2,
        letterSpacing: "-0.005em",
        color: T.textOnInverse,
        marginTop: 18,
        marginBottom: 6,
      }}
    >
      {children}
    </h4>
  );
}

function List({
  T, items,
}: { T: ReturnType<typeof useThemeTokens>; items: React.ReactNode[] }) {
  return (
    <ul style={{ marginTop: 8, paddingLeft: 18 }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            color: T.textMuted, fontSize: "0.92rem", lineHeight: 1.55, marginTop: 6,
            listStyleType: "disc",
          }}
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function OrderedList({
  T, items,
}: { T: ReturnType<typeof useThemeTokens>; items: React.ReactNode[] }) {
  return (
    <ol style={{ marginTop: 8, paddingLeft: 22 }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            color: T.textMuted, fontSize: "0.92rem", lineHeight: 1.55, marginTop: 10,
            listStyleType: "decimal",
          }}
        >
          {item}
        </li>
      ))}
    </ol>
  );
}

function UList({
  T, items, onInverse,
}: {
  T: ReturnType<typeof useThemeTokens>;
  items: React.ReactNode[];
  onInverse?: boolean;
}) {
  const color = onInverse ? T.textMutedOnInverse : T.textMuted;
  return (
    <ul style={{ marginTop: 8, paddingLeft: 18 }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            color, fontSize: "0.9rem", lineHeight: 1.55, marginTop: 5,
            listStyleType: "disc",
          }}
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Note({
  T, children,
}: { T: ReturnType<typeof useThemeTokens>; children: React.ReactNode }) {
  return (
    <p
      style={{
        color: T.textMuted,
        fontSize: "0.82rem",
        lineHeight: 1.55,
        fontStyle: "italic",
        marginTop: 10,
        padding: "10px 14px",
        borderLeft: `2px solid ${T.gold}`,
        background: T.chipBg,
        borderRadius: "0 4px 4px 0",
      }}
    >
      {children}
    </p>
  );
}

function Code({
  T, children,
}: { T: ReturnType<typeof useThemeTokens>; children: React.ReactNode }) {
  return (
    <code
      style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.78em",
        background: T.codeBg,
        padding: "1px 5px",
        borderRadius: 3,
        color: T.text,
      }}
    >
      {children}
    </code>
  );
}
